import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Loader2 } from "lucide-react";

import { authClient } from "@/lib/auth-client";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import { useContractings } from "@/hooks/queries/useContractings";
import { useDashboardData } from "@/hooks/queries/useDashboardData";
import { profileToFilters } from "@/lib/profile-filters";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

import { ConfigureLineBanner } from "@/components/onboarding/ConfigureLineBanner";
import { QualificationBanner } from "@/components/dashboard/QualificationBanner";
import {
  QualificationFilterChips,
  type QualificationFilter,
} from "@/components/dashboard/QualificationFilterChips";
import { MobileProfileToggle } from "@/components/dashboard/MobileProfileToggle";
import { SearchProfileSidebar } from "@/components/profiles/SearchProfileSidebar";
import { ProfileCard } from "@/components/filters/ProfileCard";
import { ContractingTable } from "@/components/contractings/ContractingTable";
import { UnlockPageButton } from "@/components/dashboard/UnlockPageButton";
import { useAnalyzesOnUnlock } from "@/hooks/useQualificationAnalysis";
import { ContractingDetailSheet } from "@/components/contractings/ContractingDetailSheet";
import { Paginator } from "@/components/common/Paginator";
import { AiChatButton } from "@/components/common/AiChatButton";
import {
  ScheduleMeetingCard,
  ScheduleMeetingRow,
} from "@/components/common/ScheduleMeetingCard";
import { Skeleton } from "@/components/ui/skeleton";

// Recharts is heavy — split it out of the main bundle.
const DashboardStats = lazy(() =>
  import("@/components/dashboard/DashboardStats").then((m) => ({
    default: m.DashboardStats,
  })),
);

const PAGE_SIZE = 10;

// "Calificas en N de M" arriba del listado. Apagado momentáneamente el 30-oct-2026 a
// pedido de Abel: vuelve con `true`.
const SHOW_QUALIFICATION_BANNER: boolean = false;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

export default function Dashboard() {
  const { data: session } = authClient.useSession();
  const { activeProfile, isLoading: profilesLoading } = useActiveProfile();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState<number | null>(null);
  // Solo aplica debajo de `lg`: en escritorio el panel está siempre desplegado.
  const [panelOpen, setPanelOpen] = useState(false);
  // El onboarding llega con "Analizadas" puesto: las obras que ya cruzó.
  const arrivalState = useLocation().state as { qualification?: QualificationFilter } | null;
  const [qualificationFilter, setQualificationFilter] = useState<QualificationFilter>(
    arrivalState?.qualification ?? null,
  );

  const firstName = (session?.user?.name || "").split(" ")[0] || "";

  const baseFilters = useMemo(
    () => (activeProfile ? profileToFilters(activeProfile) : {}),
    [activeProfile],
  );

  // Al cambiar de perfil o al editar sus filtros, el paginado vuelve al inicio:
  // la página 4 del resultado anterior no existe en el nuevo.
  // Se compara el contenido y no la referencia porque `activeProfile` sale de un
  // find() sobre datos de query: cada refetch en segundo plano trae objetos
  // nuevos, y depender de la referencia reiniciaría la página sola.
  const filtersKey = JSON.stringify(baseFilters);
  const previousFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (previousFiltersKey.current === filtersKey) return;
    // Que cargue la línea al entrar no es cambiarla: el chip con el que llegó se queda.
    const lineChanged = previousFiltersKey.current !== "{}";
    previousFiltersKey.current = filtersKey;
    setPage(0);
    if (lineChanged) setQualificationFilter(null);
  }, [filtersKey]);

  const listFilters = useMemo(
    () => ({
      ...baseFilters,
      page,
      size: pageSize,
      qualification: qualificationFilter,
    }),
    [baseFilters, page, pageSize, qualificationFilter],
  );

  function filterByQualification(value: QualificationFilter) {
    setQualificationFilter(value);
    setPage(0);
  }

  const {
    data: list,
    isLoading: listLoading,
    isPlaceholderData: listOutdated,
  } = useContractings(
    listFilters,
    !!activeProfile,
  );
  const {
    data: stats,
    isLoading: statsLoading,
    isPlaceholderData: statsOutdated,
  } = useDashboardData(
    baseFilters,
    !!activeProfile,
  );
  const analyzesOnUnlock = useAnalyzesOnUnlock();
  const selectedRow = list?.contractings.find((c) => c.contracting_id === selected);

  if (profilesLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const pagination = list?.pagination;

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      {/* Greeting */}
      <div className="mb-4 lg:mb-6">
        <p className="text-xs font-bold uppercase tracking-wide text-primary sm:text-sm">
          Descubrimiento de oportunidades
        </p>
        <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight">
          {greeting()}
          {firstName ? `, ${firstName}` : ""}! 👋
        </h1>
        {/* La explicación del marcador solo cabe en pantallas grandes; en móvil
            robaría la pantalla completa antes del primer resultado. */}
        <p className="mt-1.5 hidden text-sm text-muted-foreground lg:block">
          Explora las licitaciones que coinciden con tus líneas de negocio y{" "}
          <span className="font-semibold text-foreground/80">
            guarda las que te sirvan
          </span>{" "}
          con el marcador ☆ — pasan a{" "}
          <span className="font-semibold text-foreground/80">
            Mis licitaciones
          </span>
          .
        </p>
      </div>

      <ConfigureLineBanner />
      {SHOW_QUALIFICATION_BANNER && (
        <QualificationBanner
          lineId={activeProfile?.searchprofile_id ?? null}
          onShowQualified={() => filterByQualification("CALIFICA")}
        />
      )}

      <div className="mb-4 flex flex-col gap-3 lg:hidden">
        {activeProfile && (
          <MobileProfileToggle
            profile={activeProfile}
            open={panelOpen}
            onToggle={() => setPanelOpen((o) => !o)}
          />
        )}
        {/* Con el panel abierto estorba: quedaría entre el toggle y la lista de
            líneas que despliega. */}
        {!panelOpen && <ScheduleMeetingRow />}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        {/* Profiles sidebar */}
        <div
          className={cn("lg:w-72 lg:shrink-0", !panelOpen && "hidden lg:block")}
        >
          <div className="flex flex-col gap-4 lg:sticky lg:top-24">
            <SearchProfileSidebar />
            <div className="hidden lg:block">
              <ScheduleMeetingCard />
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 lg:gap-6">
          {activeProfile && (
            // Por línea: así lo que quedó sin guardar se manda a su línea, no a la siguiente.
            <ProfileCard
              key={activeProfile.searchprofile_id}
              profile={activeProfile}
              matchedCount={stats?.paraTi ?? 0}
              className={cn(!panelOpen && "hidden lg:flex")}
            />
          )}

          {/* Resultados */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold tracking-tight lg:text-xl">
                Resultados
              </h2>
              {(statsOutdated || listOutdated) && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Actualizando…
                </span>
              )}
            </div>
            <div
              aria-busy={statsOutdated}
              className={cn(
                "transition-opacity",
                statsOutdated && "pointer-events-none opacity-50",
              )}
            >
              <Suspense
                fallback={
                  <Skeleton className="h-[240px] w-full rounded-2xl lg:h-[400px]" />
                }
              >
                <DashboardStats data={stats} isLoading={statsLoading} />
              </Suspense>
            </div>
          </div>

          {/* Encabezado del listado: en escritorio la tabla ya trae su cabecera. */}
          <div className="flex items-baseline justify-between gap-2 lg:hidden">
            <h2 className="text-lg font-bold tracking-tight">Oportunidades</h2>
            {pagination && (
              <span className="text-xs text-muted-foreground">
                {formatNumber(pagination.length)} resultados
              </span>
            )}
          </div>

          <QualificationFilterChips
            lineId={activeProfile?.searchprofile_id ?? null}
            value={qualificationFilter}
            onChange={filterByQualification}
          />

          {/* Mientras llegan los nuevos, la página vieja no se desbloquea ni se abre. */}
          <UnlockPageButton items={listOutdated ? [] : (list?.contractings ?? [])} />

          {/* Results table */}
          <div
            aria-busy={listOutdated}
            className={cn(
              "transition-opacity lg:overflow-hidden lg:rounded-2xl lg:bg-card lg:shadow-sm",
              listOutdated && "pointer-events-none opacity-50",
            )}
          >
            <ContractingTable
              items={list?.contractings ?? []}
              isLoading={listLoading}
              onRowClick={(c) => setSelected(c.contracting_id)}
              emptyLabel="No hay nuevas licitaciones con estos filtros."
            />

            {pagination && pagination.length > 0 && (
              <Paginator
                page={page}
                pageSize={pageSize}
                total={pagination.length}
                onPageChange={setPage}
                onPageSizeChange={(s) => {
                  setPageSize(s);
                  setPage(0);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <AiChatButton />

      <ContractingDetailSheet
        contractingId={selected}
        searchProfileId={activeProfile?.searchprofile_id}
        locked={selectedRow?.isLocked === true}
        analyzeOnUnlock={!!selectedRow && analyzesOnUnlock(selectedRow)}
        open={selected != null}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
